// docs/102 FL3–FL5 — what a flight leaves on the board: the soft shadow under a figure in the air
// (faded in and out with its height, so it never pops), the dust and air it pushes down as it takes
// off, the gust and dust as it comes down, and the eagle's snatch and drop. Quiet sounds go with
// each. Drawn with the spell textures: shadows on the ground layer under the figures, the rest in
// the board's effects layer. The effect-intensity setting thins the particles; reduce motion has no
// flights at all (the plan slides fliers like walkers).
import Phaser from 'phaser';
import { TEX, ensureSpellTextures } from '../spells/textures';
import { playSfx } from '../../core/audio';
import type { Wings } from '../../core/playback/flight';
import { oneShot } from './oneShots';

type P = { x: number; y: number };

export interface FlightFxHost {
  scene: Phaser.Scene;
  /** The board's effects layer. */
  add(o: Phaser.GameObjects.GameObject): void;
  /** Under the figures (dust and air on the ground). */
  addGround(o: Phaser.GameObjects.GameObject): void;
  /** The board's shadow layer (the wildlife's shadows); else `addGround`. */
  addShadow?(o: Phaser.GameObjects.GameObject): void;
  cell: number;
}

export interface FlightQuality { density: number; still: boolean }

const DUST = [0xd8d0bc, 0xc4b89c, 0xb0a488];
const AIR = 0xf4f8ff;
const FEATHERS = [0x5a3a1e, 0x7a5230, 0x4a2e18, 0xf0ead8];
/** A shadow's texture: an even dark core with a soft rim (the glow texture's falloff left it a faint smudge). */
const SHADOW_TEX = 'fk-flight-shadow';

function ensureShadowTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(SHADOW_TEX)) return;
  const c = scene.textures.createCanvas(SHADOW_TEX, 64, 64)!;
  const x = c.getContext();
  const g = x.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, 'rgba(255,255,255,1)'); g.addColorStop(0.55, 'rgba(255,255,255,0.85)'); g.addColorStop(1, 'rgba(255,255,255,0)');
  x.fillStyle = g; x.fillRect(0, 0, 64, 64);
  c.refresh();
  c.setFilter(Phaser.Textures.FilterMode.LINEAR);
}

/** A flier's shadow on the ground: an ellipse as wide as its body, darkest in the middle. */
export class FlightShadow {
  constructor(private readonly img: Phaser.GameObjects.Image, private readonly w: number) {}

  place(p: P, alpha: number, scale: number): void {
    this.img.setPosition(p.x, p.y).setAlpha(alpha);
    this.img.setDisplaySize(this.w * scale, this.w * scale * 0.36);
  }

  destroy(): void { this.img.destroy(); }
}

export class FlightFx {
  private live = new Set<Phaser.GameObjects.GameObject>();

  constructor(private readonly host: FlightFxHost, private quality: FlightQuality) { ensureSpellTextures(host.scene); ensureShadowTexture(host.scene); }

  setQuality(q: FlightQuality): void { this.quality = q; }

  /** A skipped playback: every shadow and puff goes. */
  clear(): void {
    for (const o of this.live) o.destroy();
    this.live.clear();
  }

  /** A shadow for a body `size` squares a side, unseen until it is placed with some alpha. */
  shadow(size: number): FlightShadow {
    const w = this.host.cell * size * 0.8;
    const img = this.host.scene.add.image(0, 0, SHADOW_TEX).setTint(0x000000).setAlpha(0).setDisplaySize(w, w * 0.36);
    (this.host.addShadow ?? this.host.addGround)(img);
    this.track(img);
    const s = new FlightShadow(img, w);
    const destroy = s.destroy.bind(s);
    s.destroy = () => { this.live.delete(img); destroy(); };
    return s;
  }

  /**
   * It pushes off the ground at feet point `at`: dust rolls out along the ground and the air it
   * beats down rings out, with a soft flap (great wings: a deep low beat).
   */
  takeOff(at: P, size: number, wings: Wings, pan?: number, audible = true): void {
    const cell = this.host.cell;
    this.ring(at, cell * size * 0.5, cell * size * 1.9, 0.45, 420);
    this.dust(at, size, Math.round(6 + 4 * size), 0.5);
    this.streaks(at, size, 1);
    if (audible) playSfx(wings === 'great' ? 'move.wings' : 'wild.wings', { gain: wings === 'great' ? 0.3 : 0.75, ...(pan !== undefined ? { pan } : {}) });
  }

  /** It comes down on feet point `at`: a gust rolls out from under its wings, dust settles, a soft thump. */
  land(at: P, size: number, wings: Wings, pan?: number, audible = true): void {
    const cell = this.host.cell;
    this.ring(at, cell * size * 0.4, cell * size * 2.2, 0.55, 520);
    this.dust(at, size, Math.round(5 + 4 * size), 0.45);
    this.streaks(at, size, -1);
    if (!audible) return;
    const p = pan !== undefined ? { pan } : {};
    // the air under its wings (`trap.whoosh`: plain air whooshes; `cue.whoosh_air` was mostly flame)
    playSfx('trap.whoosh', { gain: wings === 'great' ? 0.3 : 0.16, ...p });
    // a huge body's own landing thud is the server's (`step.land_heavy`)
    if (wings !== 'great') playSfx('cue.thud', { gain: 0.14, rate: 1.5, delayMs: 60, ...p });
  }

  /** The carrier's talons close on a figure (its chest at `at`): feathers and a snatch of air. */
  grab(at: P, pan?: number, audible = true): void {
    this.burst(TEX.leaf, at, 10, { speed: { min: 30, max: 90 }, angle: { min: 0, max: 360 }, gravityY: 40, lifespan: { min: 600, max: 1000 }, rotate: { min: 0, max: 360 }, alpha: { start: 1, end: 0 }, tint: FEATHERS });
    this.ring(at, this.host.cell * 0.3, this.host.cell * 1.4, 0.6, 320);
    if (audible) playSfx('swing.light', { gain: 0.3, ...(pan !== undefined ? { pan } : {}) });   // a quick snatch of air (`cue.whoosh` held portals and fire)
  }

  /** The carried figure hits the ground at feet point `at`: dust and a thud. */
  drop(at: P, pan?: number, audible = true): void {
    this.dust(at, 1, 8, 0.55);
    this.ring(at, this.host.cell * 0.3, this.host.cell * 1.3, 0.4, 360);
    if (audible) playSfx('cue.thud', { gain: 0.35, ...(pan !== undefined ? { pan } : {}) });
  }

  // ---- parts -----------------------------------------------------------------------------------

  private track<T extends Phaser.GameObjects.GameObject>(o: T): T {
    this.live.add(o);
    o.once(Phaser.GameObjects.Events.DESTROY, () => this.live.delete(o));
    // (and on a new board, whose killed tweens would leave it hanging)
    return oneShot(this.host.scene, o);
  }

  /** A ring of air rolling out flat over the ground from `at`. */
  private ring(at: P, from: number, to: number, alpha: number, ms: number): void {
    if (this.quality.still) return;
    const r = this.track(this.host.scene.add.image(at.x, at.y - 1, TEX.ring).setTint(AIR).setAlpha(alpha * Math.max(0.4, this.quality.density)));
    r.setDisplaySize(from, from * 0.4);
    this.host.addGround(r);
    this.host.scene.tweens.add({ targets: r, displayWidth: to, displayHeight: to * 0.4, alpha: 0, duration: ms, ease: 'Quad.easeOut', onComplete: () => r.destroy() });
  }

  /** Dust thrown out sideways along the ground from under it. */
  private dust(at: P, size: number, n: number, alpha: number): void {
    const spread = this.host.cell * size * 0.35;
    this.burst(TEX.smoke, at, n, {
      x: { min: -spread, max: spread }, speed: { min: 18 + 8 * size, max: 55 + 15 * size }, angle: { min: 185, max: 355 }, gravityY: 30,
      lifespan: { min: 500, max: 900 }, scale: { start: 0.35 * size, end: 0.9 * size }, alpha: { start: alpha, end: 0 }, tint: DUST,
    }, true);
  }

  /** Streaks of air pushed down past it (`dir` 1, taking off) or swept out under it (-1, landing). */
  private streaks(at: P, size: number, dir: 1 | -1): void {
    const w = this.host.cell * size * 0.6;
    this.burst(TEX.px, { x: at.x, y: at.y - this.host.cell * size * 0.4 }, Math.round(6 + 3 * size), {
      x: { min: -w, max: w }, speedY: dir > 0 ? { min: 40, max: 90 } : { min: -10, max: 10 }, speedX: dir > 0 ? { min: -10, max: 10 } : { min: -70, max: 70 },
      lifespan: { min: 220, max: 380 }, scaleX: dir > 0 ? 0.5 : 2.5, scaleY: dir > 0 ? 2.5 : 0.5, alpha: { start: 0.6, end: 0 }, tint: AIR,
    });
  }

  private burst(tex: string, at: P, n: number, cfg: Phaser.Types.GameObjects.Particles.ParticleEmitterConfig, ground = false): void {
    if (this.quality.still || this.quality.density < 0.15) return;
    const e = this.track(this.host.scene.add.particles(at.x, at.y, tex, { ...cfg, emitting: false }));
    if (ground) this.host.addGround(e); else this.host.add(e);
    // Phaser 4: explode's x/y are emitter-relative (LESSONS: "explode() is emitter-relative")
    e.explode(Math.max(1, Math.round(n * Math.max(0.3, this.quality.density))), 0, 0);
    this.host.scene.time.delayedCall(1400, () => e.destroy());
  }
}
